import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

// Map roles to their default dashboard paths
const ROLE_DASHBOARDS: Record<string, string> = {
  RENTER: '/renter/dashboard',
  OWNER: '/owner/dashboard',
  AGENT: '/agent/dashboard',
  CONTRACTOR: '/contractor/dashboard',
  ADMIN: '/admin/dashboard',
};

function getDefaultDashboard(user: { role: string; agencyId?: number | null } | null): string {
  if (!user) return '/login';
  if (user.role === 'ADMIN' && user.agencyId) return '/agency/dashboard';
  return ROLE_DASHBOARDS[user.role] || '/dashboard';
}

// Define which path prefixes each role can access
const ROLE_PATH_PREFIXES: Record<string, string[]> = {
  RENTER: ['/renter/', '/maintenance/', '/complaints/', '/documents', '/messages', '/notifications', '/payment'],
  OWNER: ['/owner/', '/documents', '/messages', '/notifications'],
  AGENT: ['/agent/', '/documents', '/messages', '/notifications'],
  CONTRACTOR: ['/contractor/', '/messages', '/notifications'],
  ADMIN: ['/admin/', '/agency/', '/documents', '/messages', '/notifications'],
};

// Shared paths that any authenticated user can access
const SHARED_PATHS = ['/dashboard', '/messages', '/documents', '/notifications'];

export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8F8]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userRole = user.role?.toUpperCase();

  // Check if specific roles are required for this route
  if (allowedRoles && allowedRoles.length > 0) {
    const hasAllowedRole = allowedRoles.some(role => role.toUpperCase() === userRole);
    if (!hasAllowedRole) {
      // Redirect to user's own dashboard
      const redirectPath = getDefaultDashboard(user);
      return <Navigate to={redirectPath} replace />;
    }
  }

  // Check if user is trying to access another role's pages
  const isAccessingSharedPath = SHARED_PATHS.some(path => currentPath === path || currentPath.startsWith(path + '/'));

  if (!isAccessingSharedPath) {
    // Check if the current path belongs to another role
    for (const [role, prefixes] of Object.entries(ROLE_PATH_PREFIXES)) {
      if (role !== userRole) {
        const isAccessingOtherRolePath = prefixes.some(prefix => currentPath.startsWith(prefix));
        if (isAccessingOtherRolePath) {
          // Redirect to user's own dashboard
          const redirectPath = getDefaultDashboard(user);
          return <Navigate to={redirectPath} replace />;
        }
      }
    }
  }

  // User is authenticated and has access
  return <>{children}</>;
}

// Component for public-only routes (login, register) - redirects authenticated users
export function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8F8]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // If authenticated, redirect to appropriate dashboard
  if (isAuthenticated && user) {
    const userRole = user.role?.toUpperCase();
    const redirectPath = ROLE_DASHBOARDS[userRole] || '/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}

